import { cloneVNode, h, defineComponent, ref, onMounted, onUnmounted, provide, watchEffect, computed, nextTick, inject, toRaw, watch } from 'vue';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it;

  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      return function () {
        if (i >= o.length) return {
          done: true
        };
        return {
          done: false,
          value: o[i++]
        };
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  it = o[Symbol.iterator]();
  return it.next.bind(it);
}

function match(value, lookup) {
  if (value in lookup) {
    var returnValue = lookup[value];

    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }

    return typeof returnValue === 'function' ? returnValue.apply(void 0, args) : returnValue;
  }

  var error = new Error("Tried to handle \"" + value + "\" but there is no handler defined. Only defined handlers are: " + Object.keys(lookup).map(function (key) {
    return "\"" + key + "\"";
  }).join(', ') + ".");
  if (Error.captureStackTrace) Error.captureStackTrace(error, match);
  throw error;
}

var Features;

(function (Features) {
  /** No features at all */
  Features[Features["None"] = 0] = "None";
  /**
   * When used, this will allow us to use one of the render strategies.
   *
   * **The render strategies are:**
   *    - **Unmount**   _(Will unmount the component.)_
   *    - **Hidden**    _(Will hide the component using the [hidden] attribute.)_
   */

  Features[Features["RenderStrategy"] = 1] = "RenderStrategy";
  /**
   * When used, this will allow the user of our component to be in control. This can be used when
   * you want to transition based on some state.
   */

  Features[Features["Static"] = 2] = "Static";
})(Features || (Features = {}));

var RenderStrategy;

(function (RenderStrategy) {
  RenderStrategy[RenderStrategy["Unmount"] = 0] = "Unmount";
  RenderStrategy[RenderStrategy["Hidden"] = 1] = "Hidden";
})(RenderStrategy || (RenderStrategy = {}));

function render(_ref) {
  var _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? true : _ref$visible,
      _ref$features = _ref.features,
      features = _ref$features === void 0 ? Features.None : _ref$features,
      main = _objectWithoutPropertiesLoose(_ref, ["visible", "features"]);

  // Visible always render
  if (visible) return _render(main);

  if (features & Features.Static) {
    // When the `static` prop is passed as `true`, then the user is in control, thus we don't care about anything else
    if (main.props["static"]) return _render(main);
  }

  if (features & Features.RenderStrategy) {
    var _main$props$unmount, _match;

    var strategy = ((_main$props$unmount = main.props.unmount) != null ? _main$props$unmount : true) ? RenderStrategy.Unmount : RenderStrategy.Hidden;
    return match(strategy, (_match = {}, _match[RenderStrategy.Unmount] = function () {
      return null;
    }, _match[RenderStrategy.Hidden] = function () {
      return _render(_extends({}, main, {
        props: _extends({}, main.props, {
          hidden: true,
          style: {
            display: 'none'
          }
        })
      }));
    }, _match));
  } // No features enabled, just render


  return _render(main);
}

function _render(_ref2) {
  var props = _ref2.props,
      attrs = _ref2.attrs,
      slots = _ref2.slots,
      slot = _ref2.slot;

  var _omit = omit(props, ['unmount', 'static']),
      as = _omit.as,
      passThroughProps = _objectWithoutPropertiesLoose(_omit, ["as"]);

  var children = slots["default"] == null ? void 0 : slots["default"](slot);

  if (as === 'template') {
    if (Object.keys(passThroughProps).length > 0 || 'class' in attrs) {
      var _ref3 = children != null ? children : [],
          firstChild = _ref3[0],
          other = _ref3.slice(1);

      if (other.length > 0) throw new Error('You should only render 1 child or use the `as="..."` prop');
      return cloneVNode(firstChild, passThroughProps);
    }

    return children;
  }

  return h(as, passThroughProps, children);
}

function omit(object, keysToOmit) {
  if (keysToOmit === void 0) {
    keysToOmit = [];
  }

  var clone = Object.assign({}, object);

  for (var _iterator = _createForOfIteratorHelperLoose(keysToOmit), _step; !(_step = _iterator()).done;) {
    var key = _step.value;
    if (key in clone) delete clone[key];
  }

  return clone;
}

var id = 0;

function generateId() {
  return ++id;
}

function useId() {
  return generateId();
}

// TODO: This must already exist somewhere, right? 🤔
// Ref: https://www.w3.org/TR/uievents-key/#named-key-attribute-values
var Keys;

(function (Keys) {
  Keys["Space"] = " ";
  Keys["Enter"] = "Enter";
  Keys["Escape"] = "Escape";
  Keys["Backspace"] = "Backspace";
  Keys["ArrowUp"] = "ArrowUp";
  Keys["ArrowDown"] = "ArrowDown";
  Keys["Home"] = "Home";
  Keys["End"] = "End";
  Keys["PageUp"] = "PageUp";
  Keys["PageDown"] = "PageDown";
  Keys["Tab"] = "Tab";
})(Keys || (Keys = {}));

function assertNever(x) {
  throw new Error('Unexpected object: ' + x);
}

var Focus;

(function (Focus) {
  /** Focus the first non-disabled item. */
  Focus[Focus["First"] = 0] = "First";
  /** Focus the previous non-disabled item. */

  Focus[Focus["Previous"] = 1] = "Previous";
  /** Focus the next non-disabled item. */

  Focus[Focus["Next"] = 2] = "Next";
  /** Focus the last non-disabled item. */

  Focus[Focus["Last"] = 3] = "Last";
  /** Focus a specific item based on the `id` of the item. */

  Focus[Focus["Specific"] = 4] = "Specific";
  /** Focus no items at all. */

  Focus[Focus["Nothing"] = 5] = "Nothing";
})(Focus || (Focus = {}));

function calculateActiveIndex(action, resolvers) {
  var items = resolvers.resolveItems();
  if (items.length <= 0) return null;
  var currentActiveIndex = resolvers.resolveActiveIndex();
  var activeIndex = currentActiveIndex != null ? currentActiveIndex : -1;

  var nextActiveIndex = function () {
    switch (action.focus) {
      case Focus.First:
        return items.findIndex(function (item) {
          return !resolvers.resolveDisabled(item);
        });

      case Focus.Previous:
        {
          var idx = items.slice().reverse().findIndex(function (item, idx, all) {
            if (activeIndex !== -1 && all.length - idx - 1 >= activeIndex) return false;
            return !resolvers.resolveDisabled(item);
          });
          if (idx === -1) return idx;
          return items.length - 1 - idx;
        }

      case Focus.Next:
        return items.findIndex(function (item, idx) {
          if (idx <= activeIndex) return false;
          return !resolvers.resolveDisabled(item);
        });

      case Focus.Last:
        {
          var _idx = items.slice().reverse().findIndex(function (item) {
            return !resolvers.resolveDisabled(item);
          });

          if (_idx === -1) return _idx;
          return items.length - 1 - _idx;
        }

      case Focus.Specific:
        return items.findIndex(function (item) {
          return resolvers.resolveId(item) === action.id;
        });

      case Focus.Nothing:
        return null;

      default:
        assertNever(action);
    }
  }();

  return nextActiveIndex === -1 ? currentActiveIndex : nextActiveIndex;
}

function resolvePropValue(property, bag) {
  if (property === undefined) return undefined;
  if (typeof property === 'function') return property(bag);
  return property;
}

var MenuStates;

(function (MenuStates) {
  MenuStates[MenuStates["Open"] = 0] = "Open";
  MenuStates[MenuStates["Closed"] = 1] = "Closed";
})(MenuStates || (MenuStates = {}));

function nextFrame(cb) {
  requestAnimationFrame(function () {
    return requestAnimationFrame(cb);
  });
}

var MenuContext = /*#__PURE__*/Symbol('MenuContext');

function useMenuContext(component) {
  var context = inject(MenuContext, null);

  if (context === null) {
    var err = new Error("<" + component + " /> is missing a parent <Menu /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useMenuContext);
    throw err;
  }

  return context;
}

var Menu = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs;
    var menuState = ref(MenuStates.Closed);
    var buttonRef = ref(null);
    var itemsRef = ref(null);
    var items = ref([]);
    var searchQuery = ref('');
    var activeItemIndex = ref(null);
    var api = {
      menuState: menuState,
      buttonRef: buttonRef,
      itemsRef: itemsRef,
      items: items,
      searchQuery: searchQuery,
      activeItemIndex: activeItemIndex,
      closeMenu: function closeMenu() {
        menuState.value = MenuStates.Closed;
        activeItemIndex.value = null;
      },
      openMenu: function openMenu() {
        return menuState.value = MenuStates.Open;
      },
      goToItem: function goToItem(focus, id) {
        var nextActiveItemIndex = calculateActiveIndex(focus === Focus.Specific ? {
          focus: Focus.Specific,
          id: id
        } : {
          focus: focus
        }, {
          resolveItems: function resolveItems() {
            return items.value;
          },
          resolveActiveIndex: function resolveActiveIndex() {
            return activeItemIndex.value;
          },
          resolveId: function resolveId(item) {
            return item.id;
          },
          resolveDisabled: function resolveDisabled(item) {
            return item.dataRef.disabled;
          }
        });
        if (searchQuery.value === '' && activeItemIndex.value === nextActiveItemIndex) return;
        searchQuery.value = '';
        activeItemIndex.value = nextActiveItemIndex;
      },
      search: function search(value) {
        searchQuery.value += value;
        var match = items.value.findIndex(function (item) {
          return item.dataRef.textValue.startsWith(searchQuery.value) && !item.dataRef.disabled;
        });
        if (match === -1 || match === activeItemIndex.value) return;
        activeItemIndex.value = match;
      },
      clearSearch: function clearSearch() {
        searchQuery.value = '';
      },
      registerItem: function registerItem(id, dataRef) {
        // @ts-expect-error The expected type comes from property 'dataRef' which is declared here on type '{ id: string; dataRef: { textValue: string; disabled: boolean; }; }'
        items.value.push({
          id: id,
          dataRef: dataRef
        });
      },
      unregisterItem: function unregisterItem(id) {
        var nextItems = items.value.slice();
        var currentActiveItem = activeItemIndex.value !== null ? nextItems[activeItemIndex.value] : null;
        var idx = nextItems.findIndex(function (a) {
          return a.id === id;
        });
        if (idx !== -1) nextItems.splice(idx, 1);
        items.value = nextItems;

        activeItemIndex.value = function () {
          if (idx === activeItemIndex.value) return null;
          if (currentActiveItem === null) return null; // If we removed the item before the actual active index, then it would be out of sync. To
          // fix this, we will find the correct (new) index position.

          return nextItems.indexOf(currentActiveItem);
        }();
      }
    };
    onMounted(function () {
      function handler(event) {
        var _buttonRef$value, _itemsRef$value, _buttonRef$value2;

        var target = event.target;
        var active = document.activeElement;
        if (menuState.value !== MenuStates.Open) return;
        if ((_buttonRef$value = buttonRef.value) == null ? void 0 : _buttonRef$value.contains(target)) return;
        if (!((_itemsRef$value = itemsRef.value) == null ? void 0 : _itemsRef$value.contains(target))) api.closeMenu();
        if (active !== document.body && (active == null ? void 0 : active.contains(target))) return; // Keep focus on newly clicked/focused element

        if (!event.defaultPrevented) (_buttonRef$value2 = buttonRef.value) == null ? void 0 : _buttonRef$value2.focus({
          preventScroll: true
        });
      }

      window.addEventListener('mousedown', handler);
      onUnmounted(function () {
        return window.removeEventListener('mousedown', handler);
      });
    }); // @ts-expect-error Types of property 'dataRef' are incompatible.

    provide(MenuContext, api);
    return function () {
      var slot = {
        open: menuState.value === MenuStates.Open
      };
      return render({
        props: props,
        slot: slot,
        slots: slots,
        attrs: attrs
      });
    };
  }
});
var MenuButton = /*#__PURE__*/defineComponent({
  props: {
    disabled: {
      type: Boolean,
      "default": false
    },
    as: {
      type: [Object, String],
      "default": 'button'
    }
  },
  render: function render$1() {
    var _api$itemsRef$value;

    var api = useMenuContext('MenuButton');
    var slot = {
      open: api.menuState.value === MenuStates.Open
    };
    var propsWeControl = {
      ref: 'el',
      id: this.id,
      type: 'button',
      'aria-haspopup': true,
      'aria-controls': (_api$itemsRef$value = api.itemsRef.value) == null ? void 0 : _api$itemsRef$value.id,
      'aria-expanded': api.menuState.value === MenuStates.Open ? true : undefined,
      onKeyDown: this.handleKeyDown,
      onClick: this.handleClick
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup(props) {
    var api = useMenuContext('MenuButton');
    var id = "headlessui-menu-button-" + useId();

    function handleKeyDown(event) {
      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-13
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault();
          api.openMenu();
          nextTick(function () {
            var _api$itemsRef$value2;

            (_api$itemsRef$value2 = api.itemsRef.value) == null ? void 0 : _api$itemsRef$value2.focus({
              preventScroll: true
            });
            api.goToItem(Focus.First);
          });
          break;

        case Keys.ArrowUp:
          event.preventDefault();
          api.openMenu();
          nextTick(function () {
            var _api$itemsRef$value3;

            (_api$itemsRef$value3 = api.itemsRef.value) == null ? void 0 : _api$itemsRef$value3.focus({
              preventScroll: true
            });
            api.goToItem(Focus.Last);
          });
          break;
      }
    }

    function handleClick(event) {
      if (props.disabled) return;

      if (api.menuState.value === MenuStates.Open) {
        api.closeMenu();
        nextTick(function () {
          var _api$buttonRef$value;

          return (_api$buttonRef$value = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value.focus({
            preventScroll: true
          });
        });
      } else {
        event.preventDefault();
        api.openMenu();
        nextFrame(function () {
          var _api$itemsRef$value4;

          return (_api$itemsRef$value4 = api.itemsRef.value) == null ? void 0 : _api$itemsRef$value4.focus({
            preventScroll: true
          });
        });
      }
    }

    return {
      id: id,
      el: api.buttonRef,
      handleKeyDown: handleKeyDown,
      handleClick: handleClick
    };
  }
});
var MenuItems = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'div'
    },
    "static": {
      type: Boolean,
      "default": false
    },
    unmount: {
      type: Boolean,
      "default": true
    }
  },
  render: function render$1() {
    var _api$items$value$api$, _api$buttonRef$value2;

    var api = useMenuContext('MenuItems');
    var slot = {
      open: api.menuState.value === MenuStates.Open
    };
    var propsWeControl = {
      'aria-activedescendant': api.activeItemIndex.value === null ? undefined : (_api$items$value$api$ = api.items.value[api.activeItemIndex.value]) == null ? void 0 : _api$items$value$api$.id,
      'aria-labelledby': (_api$buttonRef$value2 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value2.id,
      id: this.id,
      onKeyDown: this.handleKeyDown,
      role: 'menu',
      tabIndex: 0,
      ref: 'el'
    };
    var passThroughProps = this.$props;
    return render({
      props: _extends({}, passThroughProps, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots,
      features: Features.RenderStrategy | Features.Static,
      visible: slot.open
    });
  },
  setup: function setup() {
    var api = useMenuContext('MenuItems');
    var id = "headlessui-menu-items-" + useId();
    var searchDebounce = ref(null);
    watchEffect(function () {
      var container = api.itemsRef.value;
      if (!container) return;
      if (api.menuState.value !== MenuStates.Open) return;
      var walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
        acceptNode: function acceptNode(node) {
          if (node.getAttribute('role') === 'menuitem') return NodeFilter.FILTER_REJECT;
          if (node.hasAttribute('role')) return NodeFilter.FILTER_SKIP;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      while (walker.nextNode()) {
        walker.currentNode.setAttribute('role', 'none');
      }
    });

    function handleKeyDown(event) {
      if (searchDebounce.value) clearTimeout(searchDebounce.value);

      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-12
        // @ts-expect-error Fallthrough is expected here
        case Keys.Space:
          if (api.searchQuery.value !== '') {
            event.preventDefault();
            return api.search(event.key);
          }

        // When in type ahead mode, fallthrough

        case Keys.Enter:
          event.preventDefault();

          if (api.activeItemIndex.value !== null) {
            var _document$getElementB;

            var _id = api.items.value[api.activeItemIndex.value].id;
            (_document$getElementB = document.getElementById(_id)) == null ? void 0 : _document$getElementB.click();
          }

          api.closeMenu();
          nextTick(function () {
            var _api$buttonRef$value3;

            return (_api$buttonRef$value3 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value3.focus({
              preventScroll: true
            });
          });
          break;

        case Keys.ArrowDown:
          event.preventDefault();
          return api.goToItem(Focus.Next);

        case Keys.ArrowUp:
          event.preventDefault();
          return api.goToItem(Focus.Previous);

        case Keys.Home:
        case Keys.PageUp:
          event.preventDefault();
          return api.goToItem(Focus.First);

        case Keys.End:
        case Keys.PageDown:
          event.preventDefault();
          return api.goToItem(Focus.Last);

        case Keys.Escape:
          event.preventDefault();
          api.closeMenu();
          nextTick(function () {
            var _api$buttonRef$value4;

            return (_api$buttonRef$value4 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value4.focus({
              preventScroll: true
            });
          });
          break;

        case Keys.Tab:
          return event.preventDefault();

        default:
          if (event.key.length === 1) {
            api.search(event.key);
            searchDebounce.value = setTimeout(function () {
              return api.clearSearch();
            }, 350);
          }

          break;
      }
    }

    return {
      id: id,
      el: api.itemsRef,
      handleKeyDown: handleKeyDown
    };
  }
});
var MenuItem = /*#__PURE__*/defineComponent({
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    },
    disabled: {
      type: Boolean,
      "default": false
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  setup: function setup(props, _ref2) {
    var slots = _ref2.slots,
        attrs = _ref2.attrs;
    var api = useMenuContext('MenuItem');
    var id = "headlessui-menu-item-" + useId();
    var disabled = props.disabled,
        defaultClass = props["class"],
        _props$className = props.className,
        className = _props$className === void 0 ? defaultClass : _props$className;
    var active = computed(function () {
      return api.activeItemIndex.value !== null ? api.items.value[api.activeItemIndex.value].id === id : false;
    });
    var dataRef = ref({
      disabled: disabled,
      textValue: ''
    });
    onMounted(function () {
      var _document$getElementB2, _document$getElementB3;

      var textValue = (_document$getElementB2 = document.getElementById(id)) == null ? void 0 : (_document$getElementB3 = _document$getElementB2.textContent) == null ? void 0 : _document$getElementB3.toLowerCase().trim();
      if (textValue !== undefined) dataRef.value.textValue = textValue;
    });
    onMounted(function () {
      return api.registerItem(id, dataRef);
    });
    onUnmounted(function () {
      return api.unregisterItem(id);
    });
    watchEffect(function () {
      if (api.menuState.value !== MenuStates.Open) return;
      if (!active.value) return;
      nextTick(function () {
        var _document$getElementB4;

        return (_document$getElementB4 = document.getElementById(id)) == null ? void 0 : _document$getElementB4.scrollIntoView == null ? void 0 : _document$getElementB4.scrollIntoView({
          block: 'nearest'
        });
      });
    });

    function handleClick(event) {
      if (disabled) return event.preventDefault();
      api.closeMenu();
      nextTick(function () {
        var _api$buttonRef$value5;

        return (_api$buttonRef$value5 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value5.focus({
          preventScroll: true
        });
      });
    }

    function handleFocus() {
      if (disabled) return api.goToItem(Focus.Nothing);
      api.goToItem(Focus.Specific, id);
    }

    function handleMove() {
      if (disabled) return;
      if (active.value) return;
      api.goToItem(Focus.Specific, id);
    }

    function handleLeave() {
      if (disabled) return;
      if (!active.value) return;
      api.goToItem(Focus.Nothing);
    }

    return function () {
      var slot = {
        active: active.value,
        disabled: disabled
      };
      var propsWeControl = {
        id: id,
        role: 'menuitem',
        tabIndex: -1,
        "class": resolvePropValue(className, slot),
        'aria-disabled': disabled === true ? true : undefined,
        onClick: handleClick,
        onFocus: handleFocus,
        onPointerMove: handleMove,
        onMouseMove: handleMove,
        onPointerLeave: handleLeave,
        onMouseLeave: handleLeave
      };
      return render({
        props: _extends({}, props, propsWeControl),
        slot: slot,
        attrs: attrs,
        slots: slots
      });
    };
  }
});

var ListboxStates;

(function (ListboxStates) {
  ListboxStates[ListboxStates["Open"] = 0] = "Open";
  ListboxStates[ListboxStates["Closed"] = 1] = "Closed";
})(ListboxStates || (ListboxStates = {}));

function nextFrame$1(cb) {
  requestAnimationFrame(function () {
    return requestAnimationFrame(cb);
  });
}

var ListboxContext = /*#__PURE__*/Symbol('ListboxContext');

function useListboxContext(component) {
  var context = inject(ListboxContext, null);

  if (context === null) {
    var err = new Error("<" + component + " /> is missing a parent <Listbox /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useListboxContext);
    throw err;
  }

  return context;
} // ---


var Listbox = /*#__PURE__*/defineComponent({
  name: 'Listbox',
  emits: ['update:modelValue'],
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    },
    disabled: {
      type: [Boolean],
      "default": false
    },
    modelValue: {
      type: [Object, String, Number, Boolean],
      "default": null
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs,
        emit = _ref.emit;

    var disabled = props.disabled,
        passThroughProps = _objectWithoutPropertiesLoose(props, ["modelValue", "disabled"]);

    var listboxState = ref(ListboxStates.Closed);
    var labelRef = ref(null);
    var buttonRef = ref(null);
    var optionsRef = ref(null);
    var options = ref([]);
    var searchQuery = ref('');
    var activeOptionIndex = ref(null);
    var value = computed(function () {
      return props.modelValue;
    });
    var api = {
      listboxState: listboxState,
      value: value,
      labelRef: labelRef,
      buttonRef: buttonRef,
      optionsRef: optionsRef,
      disabled: disabled,
      options: options,
      searchQuery: searchQuery,
      activeOptionIndex: activeOptionIndex,
      closeListbox: function closeListbox() {
        if (disabled) return;
        if (listboxState.value === ListboxStates.Closed) return;
        listboxState.value = ListboxStates.Closed;
        activeOptionIndex.value = null;
      },
      openListbox: function openListbox() {
        if (disabled) return;
        if (listboxState.value === ListboxStates.Open) return;
        listboxState.value = ListboxStates.Open;
      },
      goToOption: function goToOption(focus, id) {
        if (disabled) return;
        if (listboxState.value === ListboxStates.Closed) return;
        var nextActiveOptionIndex = calculateActiveIndex(focus === Focus.Specific ? {
          focus: Focus.Specific,
          id: id
        } : {
          focus: focus
        }, {
          resolveItems: function resolveItems() {
            return options.value;
          },
          resolveActiveIndex: function resolveActiveIndex() {
            return activeOptionIndex.value;
          },
          resolveId: function resolveId(option) {
            return option.id;
          },
          resolveDisabled: function resolveDisabled(option) {
            return option.dataRef.disabled;
          }
        });
        if (searchQuery.value === '' && activeOptionIndex.value === nextActiveOptionIndex) return;
        searchQuery.value = '';
        activeOptionIndex.value = nextActiveOptionIndex;
      },
      search: function search(value) {
        if (disabled) return;
        if (listboxState.value === ListboxStates.Closed) return;
        searchQuery.value += value;
        var match = options.value.findIndex(function (option) {
          return !option.dataRef.disabled && option.dataRef.textValue.startsWith(searchQuery.value);
        });
        if (match === -1 || match === activeOptionIndex.value) return;
        activeOptionIndex.value = match;
      },
      clearSearch: function clearSearch() {
        if (disabled) return;
        if (listboxState.value === ListboxStates.Closed) return;
        if (searchQuery.value === '') return;
        searchQuery.value = '';
      },
      registerOption: function registerOption(id, dataRef) {
        // @ts-expect-error The expected type comes from property 'dataRef' which is declared here on type '{ id: string; dataRef: { textValue: string; disabled: boolean; }; }'
        options.value.push({
          id: id,
          dataRef: dataRef
        });
      },
      unregisterOption: function unregisterOption(id) {
        var nextOptions = options.value.slice();
        var currentActiveOption = activeOptionIndex.value !== null ? nextOptions[activeOptionIndex.value] : null;
        var idx = nextOptions.findIndex(function (a) {
          return a.id === id;
        });
        if (idx !== -1) nextOptions.splice(idx, 1);
        options.value = nextOptions;

        activeOptionIndex.value = function () {
          if (idx === activeOptionIndex.value) return null;
          if (currentActiveOption === null) return null; // If we removed the option before the actual active index, then it would be out of sync. To
          // fix this, we will find the correct (new) index position.

          return nextOptions.indexOf(currentActiveOption);
        }();
      },
      select: function select(value) {
        if (disabled) return;
        emit('update:modelValue', value);
      }
    };
    onMounted(function () {
      function handler(event) {
        var _buttonRef$value, _optionsRef$value, _buttonRef$value2;

        var target = event.target;
        var active = document.activeElement;
        if (listboxState.value !== ListboxStates.Open) return;
        if ((_buttonRef$value = buttonRef.value) == null ? void 0 : _buttonRef$value.contains(target)) return;
        if (!((_optionsRef$value = optionsRef.value) == null ? void 0 : _optionsRef$value.contains(target))) api.closeListbox();
        if (active !== document.body && (active == null ? void 0 : active.contains(target))) return; // Keep focus on newly clicked/focused element

        if (!event.defaultPrevented) (_buttonRef$value2 = buttonRef.value) == null ? void 0 : _buttonRef$value2.focus({
          preventScroll: true
        });
      }

      window.addEventListener('mousedown', handler);
      onUnmounted(function () {
        return window.removeEventListener('mousedown', handler);
      });
    }); // @ts-expect-error Types of property 'dataRef' are incompatible.

    provide(ListboxContext, api);
    return function () {
      var slot = {
        open: listboxState.value === ListboxStates.Open,
        disabled: disabled
      };
      return render({
        props: passThroughProps,
        slot: slot,
        slots: slots,
        attrs: attrs
      });
    };
  }
}); // ---

var ListboxLabel = /*#__PURE__*/defineComponent({
  name: 'ListboxLabel',
  props: {
    as: {
      type: [Object, String],
      "default": 'label'
    }
  },
  render: function render$1() {
    var api = useListboxContext('ListboxLabel');
    var slot = {
      open: api.listboxState.value === ListboxStates.Open,
      disabled: api.disabled
    };
    var propsWeControl = {
      id: this.id,
      ref: 'el',
      onClick: this.handleClick
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxLabel');
    var id = "headlessui-listbox-label-" + useId();
    return {
      id: id,
      el: api.labelRef,
      handleClick: function handleClick() {
        var _api$buttonRef$value;

        (_api$buttonRef$value = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value.focus({
          preventScroll: true
        });
      }
    };
  }
}); // ---

var ListboxButton = /*#__PURE__*/defineComponent({
  name: 'ListboxButton',
  props: {
    as: {
      type: [Object, String],
      "default": 'button'
    }
  },
  render: function render$1() {
    var _api$optionsRef$value;

    var api = useListboxContext('ListboxButton');
    var slot = {
      open: api.listboxState.value === ListboxStates.Open,
      disabled: api.disabled
    };
    var propsWeControl = {
      ref: 'el',
      id: this.id,
      type: 'button',
      'aria-haspopup': true,
      'aria-controls': (_api$optionsRef$value = api.optionsRef.value) == null ? void 0 : _api$optionsRef$value.id,
      'aria-expanded': api.listboxState.value === ListboxStates.Open ? true : undefined,
      'aria-labelledby': api.labelRef.value ? [api.labelRef.value.id, this.id].join(' ') : undefined,
      disabled: api.disabled,
      onKeyDown: this.handleKeyDown,
      onClick: this.handleClick
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxButton');
    var id = "headlessui-listbox-button-" + useId();

    function handleKeyDown(event) {
      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-13
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault();
          api.openListbox();
          nextTick(function () {
            var _api$optionsRef$value2;

            (_api$optionsRef$value2 = api.optionsRef.value) == null ? void 0 : _api$optionsRef$value2.focus({
              preventScroll: true
            });
            if (!api.value.value) api.goToOption(Focus.First);
          });
          break;

        case Keys.ArrowUp:
          event.preventDefault();
          api.openListbox();
          nextTick(function () {
            var _api$optionsRef$value3;

            (_api$optionsRef$value3 = api.optionsRef.value) == null ? void 0 : _api$optionsRef$value3.focus({
              preventScroll: true
            });
            if (!api.value.value) api.goToOption(Focus.Last);
          });
          break;
      }
    }

    function handleClick(event) {
      if (api.disabled) return;

      if (api.listboxState.value === ListboxStates.Open) {
        api.closeListbox();
        nextTick(function () {
          var _api$buttonRef$value2;

          return (_api$buttonRef$value2 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value2.focus({
            preventScroll: true
          });
        });
      } else {
        event.preventDefault();
        api.openListbox();
        nextFrame$1(function () {
          var _api$optionsRef$value4;

          return (_api$optionsRef$value4 = api.optionsRef.value) == null ? void 0 : _api$optionsRef$value4.focus({
            preventScroll: true
          });
        });
      }
    }

    return {
      id: id,
      el: api.buttonRef,
      handleKeyDown: handleKeyDown,
      handleClick: handleClick
    };
  }
}); // ---

var ListboxOptions = /*#__PURE__*/defineComponent({
  name: 'ListboxOptions',
  props: {
    as: {
      type: [Object, String],
      "default": 'ul'
    },
    "static": {
      type: Boolean,
      "default": false
    },
    unmount: {
      type: Boolean,
      "default": true
    }
  },
  render: function render$1() {
    var _api$options$value$ap, _api$labelRef$value$i, _api$labelRef$value, _api$buttonRef$value3;

    var api = useListboxContext('ListboxOptions');
    var slot = {
      open: api.listboxState.value === ListboxStates.Open
    };
    var propsWeControl = {
      'aria-activedescendant': api.activeOptionIndex.value === null ? undefined : (_api$options$value$ap = api.options.value[api.activeOptionIndex.value]) == null ? void 0 : _api$options$value$ap.id,
      'aria-labelledby': (_api$labelRef$value$i = (_api$labelRef$value = api.labelRef.value) == null ? void 0 : _api$labelRef$value.id) != null ? _api$labelRef$value$i : (_api$buttonRef$value3 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value3.id,
      id: this.id,
      onKeyDown: this.handleKeyDown,
      role: 'listbox',
      tabIndex: 0,
      ref: 'el'
    };
    var passThroughProps = this.$props;
    return render({
      props: _extends({}, passThroughProps, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots,
      features: Features.RenderStrategy | Features.Static,
      visible: slot.open
    });
  },
  setup: function setup() {
    var api = useListboxContext('ListboxOptions');
    var id = "headlessui-listbox-options-" + useId();
    var searchDebounce = ref(null);

    function handleKeyDown(event) {
      if (searchDebounce.value) clearTimeout(searchDebounce.value);

      switch (event.key) {
        // Ref: https://www.w3.org/TR/wai-aria-practices-1.2/#keyboard-interaction-12
        // @ts-expect-error Fallthrough is expected here
        case Keys.Space:
          if (api.searchQuery.value !== '') {
            event.preventDefault();
            return api.search(event.key);
          }

        // When in type ahead mode, fallthrough

        case Keys.Enter:
          event.preventDefault();

          if (api.activeOptionIndex.value !== null) {
            var dataRef = api.options.value[api.activeOptionIndex.value].dataRef;
            api.select(dataRef.value);
          }

          api.closeListbox();
          nextTick(function () {
            var _api$buttonRef$value4;

            return (_api$buttonRef$value4 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value4.focus({
              preventScroll: true
            });
          });
          break;

        case Keys.ArrowDown:
          event.preventDefault();
          return api.goToOption(Focus.Next);

        case Keys.ArrowUp:
          event.preventDefault();
          return api.goToOption(Focus.Previous);

        case Keys.Home:
        case Keys.PageUp:
          event.preventDefault();
          return api.goToOption(Focus.First);

        case Keys.End:
        case Keys.PageDown:
          event.preventDefault();
          return api.goToOption(Focus.Last);

        case Keys.Escape:
          event.preventDefault();
          api.closeListbox();
          nextTick(function () {
            var _api$buttonRef$value5;

            return (_api$buttonRef$value5 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value5.focus({
              preventScroll: true
            });
          });
          break;

        case Keys.Tab:
          return event.preventDefault();

        default:
          if (event.key.length === 1) {
            api.search(event.key);
            searchDebounce.value = setTimeout(function () {
              return api.clearSearch();
            }, 350);
          }

          break;
      }
    }

    return {
      id: id,
      el: api.optionsRef,
      handleKeyDown: handleKeyDown
    };
  }
});
var ListboxOption = /*#__PURE__*/defineComponent({
  name: 'ListboxOption',
  props: {
    as: {
      type: [Object, String],
      "default": 'li'
    },
    value: {
      type: [Object, String],
      "default": null
    },
    disabled: {
      type: Boolean,
      "default": false
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  setup: function setup(props, _ref2) {
    var slots = _ref2.slots,
        attrs = _ref2.attrs;
    var api = useListboxContext('ListboxOption');
    var id = "headlessui-listbox-option-" + useId();
    var disabled = props.disabled,
        defaultClass = props["class"],
        _props$className = props.className,
        className = _props$className === void 0 ? defaultClass : _props$className,
        value = props.value;
    var active = computed(function () {
      return api.activeOptionIndex.value !== null ? api.options.value[api.activeOptionIndex.value].id === id : false;
    });
    var selected = computed(function () {
      return toRaw(api.value.value) === toRaw(value);
    });
    var dataRef = ref({
      disabled: disabled,
      value: value,
      textValue: ''
    });
    onMounted(function () {
      var _document$getElementB, _document$getElementB2;

      var textValue = (_document$getElementB = document.getElementById(id)) == null ? void 0 : (_document$getElementB2 = _document$getElementB.textContent) == null ? void 0 : _document$getElementB2.toLowerCase().trim();
      if (textValue !== undefined) dataRef.value.textValue = textValue;
    });
    onMounted(function () {
      return api.registerOption(id, dataRef);
    });
    onUnmounted(function () {
      return api.unregisterOption(id);
    });
    onMounted(function () {
      watch([api.listboxState, selected], function () {
        var _document$getElementB3;

        if (api.listboxState.value !== ListboxStates.Open) return;
        if (!selected.value) return;
        api.goToOption(Focus.Specific, id);
        (_document$getElementB3 = document.getElementById(id)) == null ? void 0 : _document$getElementB3.focus == null ? void 0 : _document$getElementB3.focus();
      }, {
        immediate: true
      });
    });
    watchEffect(function () {
      if (api.listboxState.value !== ListboxStates.Open) return;
      if (!active.value) return;
      nextTick(function () {
        var _document$getElementB4;

        return (_document$getElementB4 = document.getElementById(id)) == null ? void 0 : _document$getElementB4.scrollIntoView == null ? void 0 : _document$getElementB4.scrollIntoView({
          block: 'nearest'
        });
      });
    });

    function handleClick(event) {
      if (disabled) return event.preventDefault();
      api.select(value);
      api.closeListbox();
      nextTick(function () {
        var _api$buttonRef$value6;

        return (_api$buttonRef$value6 = api.buttonRef.value) == null ? void 0 : _api$buttonRef$value6.focus({
          preventScroll: true
        });
      });
    }

    function handleFocus() {
      if (disabled) return api.goToOption(Focus.Nothing);
      api.goToOption(Focus.Specific, id);
    }

    function handleMove() {
      if (disabled) return;
      if (active.value) return;
      api.goToOption(Focus.Specific, id);
    }

    function handleLeave() {
      if (disabled) return;
      if (!active.value) return;
      api.goToOption(Focus.Nothing);
    }

    return function () {
      var slot = {
        active: active.value,
        selected: selected.value,
        disabled: disabled
      };
      var propsWeControl = {
        id: id,
        role: 'option',
        tabIndex: -1,
        "class": resolvePropValue(className, slot),
        'aria-disabled': disabled === true ? true : undefined,
        'aria-selected': selected.value === true ? selected.value : undefined,
        onClick: handleClick,
        onFocus: handleFocus,
        onPointerMove: handleMove,
        onMouseMove: handleMove,
        onPointerLeave: handleLeave,
        onMouseLeave: handleLeave
      };
      return render({
        props: _extends({}, props, propsWeControl),
        slot: slot,
        attrs: attrs,
        slots: slots
      });
    };
  }
});

var GroupContext = /*#__PURE__*/Symbol('GroupContext');

function useGroupContext(component) {
  var context = inject(GroupContext, null);

  if (context === null) {
    var err = new Error("<" + component + " /> is missing a parent <SwitchGroup /> component.");
    if (Error.captureStackTrace) Error.captureStackTrace(err, useGroupContext);
    throw err;
  }

  return context;
} // ---


var SwitchGroup = /*#__PURE__*/defineComponent({
  name: 'SwitchGroup',
  props: {
    as: {
      type: [Object, String],
      "default": 'template'
    }
  },
  setup: function setup(props, _ref) {
    var slots = _ref.slots,
        attrs = _ref.attrs;
    var switchRef = ref(null);
    var labelRef = ref(null);
    var api = {
      switchRef: switchRef,
      labelRef: labelRef
    };
    provide(GroupContext, api);
    return function () {
      return render({
        props: props,
        slot: {},
        slots: slots,
        attrs: attrs
      });
    };
  }
}); // ---

var Switch = /*#__PURE__*/defineComponent({
  name: 'Switch',
  emits: ['update:modelValue'],
  props: {
    as: {
      type: [Object, String],
      "default": 'button'
    },
    modelValue: {
      type: [Object, Boolean],
      "default": null
    },
    "class": {
      type: [String, Function],
      required: false
    },
    className: {
      type: [String, Function],
      required: false
    }
  },
  render: function render$1() {
    var api = inject(GroupContext, null);
    var _this$$props = this.$props,
        defaultClass = _this$$props["class"],
        _this$$props$classNam = _this$$props.className,
        className = _this$$props$classNam === void 0 ? defaultClass : _this$$props$classNam;
    var labelledby = computed(function () {
      var _api$labelRef$value;

      return api == null ? void 0 : (_api$labelRef$value = api.labelRef.value) == null ? void 0 : _api$labelRef$value.id;
    });
    var slot = {
      checked: this.$props.modelValue
    };
    var propsWeControl = {
      id: this.id,
      ref: api === null ? undefined : api.switchRef,
      role: 'switch',
      tabIndex: 0,
      "class": resolvePropValue(className, slot),
      'aria-checked': this.$props.modelValue,
      'aria-labelledby': labelledby.value,
      onClick: this.handleClick,
      onKeyUp: this.handleKeyUp,
      onKeyPress: this.handleKeyPress
    };

    if (this.$props.as === 'button') {
      Object.assign(propsWeControl, {
        type: 'button'
      });
    }

    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: slot,
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup(props, _ref2) {
    var emit = _ref2.emit;
    var api = inject(GroupContext, null);
    var id = "headlessui-switch-" + useId();

    function toggle() {
      emit('update:modelValue', !props.modelValue);
    }

    return {
      id: id,
      el: api == null ? void 0 : api.switchRef,
      handleClick: function handleClick(event) {
        event.preventDefault();
        toggle();
      },
      handleKeyUp: function handleKeyUp(event) {
        if (event.key !== Keys.Tab) event.preventDefault();
        if (event.key === Keys.Space) toggle();
      },
      // This is needed so that we can "cancel" the click event when we use the `Enter` key on a button.
      handleKeyPress: function handleKeyPress(event) {
        event.preventDefault();
      }
    };
  }
}); // ---

var SwitchLabel = /*#__PURE__*/defineComponent({
  name: 'SwitchLabel',
  props: {
    as: {
      type: [Object, String],
      "default": 'label'
    }
  },
  render: function render$1() {
    var propsWeControl = {
      id: this.id,
      ref: 'el',
      onClick: this.handleClick
    };
    return render({
      props: _extends({}, this.$props, propsWeControl),
      slot: {},
      attrs: this.$attrs,
      slots: this.$slots
    });
  },
  setup: function setup() {
    var api = useGroupContext('SwitchLabel');
    var id = "headlessui-switch-label-" + useId();
    return {
      id: id,
      el: api.labelRef,
      handleClick: function handleClick() {
        var _api$switchRef$value, _api$switchRef$value2;

        (_api$switchRef$value = api.switchRef.value) == null ? void 0 : _api$switchRef$value.click();
        (_api$switchRef$value2 = api.switchRef.value) == null ? void 0 : _api$switchRef$value2.focus({
          preventScroll: true
        });
      }
    };
  }
});

export { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems, Switch, SwitchGroup, SwitchLabel };
//# sourceMappingURL=headlessui.esm.js.map
